import { Source } from '../../../types';
import { OoxmlChange } from '../../../core/lib/diffs';

export interface AgentUpdate {
  agent: string;
  thought: string;
  is_final: boolean;
  completion_order?: number;
  changes?: string; // OOXML string con cambios a aplicar
}

export interface ToolResult {
  agent?: string;
  tool_name: string;
  content?: string;
  success?: boolean;
  timestamp?: string;
  tool_call_id?: string;
  metadata?: any;
}

export interface StreamingMessage {
  // Formato normal
  sources?: Source[];
  token?: string;
  done?: boolean;
  error?: string;
  changes?: string; // OOXML string with track changes
  
  // Formato agente
  agentUpdate?: AgentUpdate;
  final_response_chunk?: {
    chunk: string;
    trace_id?: string;
    sequence?: number;
    timestamp?: string;
  };
  final_response?: {
    response: string;
    sources: Source[];
    changes: string; // OOXML string with track changes
    trace_id?: string; // 🆕 Trace ID para vincular costes
  };
  
  // Resultado de herramientas
  toolResult?: ToolResult;
}

export interface StreamProcessorOptions {
  // Callbacks comunes
  onError?: (error: Error) => void;
  onComplete?: (response?: any) => void;
  
  // Para modo normal
  onToken?: (token: string) => void;
  onSources?: (sources: Source[]) => void;
  onChanges?: (changes: string) => void; // OOXML string
  
  // Para modo agente
  onAgentUpdate?: (update: AgentUpdate) => void;
  onTokenUpdate?: (agent: string, token: string, isFinal: boolean, toolResult?: any) => void; // 🆕 Agregar toolResult opcional
  onAgentChanges?: (agent: string, changes: string, thought: string) => void; // Cambios OOXML incrementales
  onFinalResponseChunk?: (chunkInfo: {
    chunk: string;
    streamedText: string;
    trace_id?: string;
    sequence?: number;
    timestamp?: string;
    finalChunkCount: number;
    lastChunkSequence: number | null;
  }) => void;
  /** No bytes for this long → onError + cancel reader (default applied in conversationService) */
  streamIdleTimeoutMs?: number;
}

/**
 * Procesador SSE unificado para chat normal y modo agente
 */
export interface CompletedThought {
  agent: string;
  thought: string;
  completion_order: number;
  toolResult?: ToolResult; // Opcional: presente cuando es un resultado de herramienta
}

// 🆕 NUEVO: Interfaz para raw steps (formato crudo del backend)
export interface RawStep {
  type: "thought" | "tool_call";
  agent: string;
  order: number;
  message?: string;  // Para type: "thought"
  output?: {         // Para type: "tool_call"
    content: string;
    success: boolean;
    metadata?: Record<string, any>;
  };
  metadata: {
    timestamp: string;
    is_final?: boolean;
    tool_name?: string;
    tool_call_id?: string;
    [key: string]: any;
  };
}

export async function processSSEStream(
  stream: ReadableStream<Uint8Array>,
  options: StreamProcessorOptions,
  isAgentMode: boolean = false
): Promise<{ content: string, sources: Source[], changes: string, completedThoughts?: CompletedThought[], rawSteps?: RawStep[], trace_id?: string }> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  
  // Variables para modo normal
  let completeContent = '';
  let sources: Source[] = [];
  let changes: string = ''; // OOXML string
  let traceId: string | undefined; // 🆕 Trace ID para modo normal
  let sourcesProcessed = false;
  let changesProcessed = false;
  
  // Variables para modo agente
  let completionCounter: number = 0;
  const completedThoughts: CompletedThought[] = [];
  const rawSteps: RawStep[] = []; // 🆕 Array para preservar steps crudos
  let finalResponse: { response: string, sources: Source[], changes: string, trace_id?: string } = { response: '', sources: [], changes: '' };
  let streamedFinalText = '';
  let finalChunkCount = 0;
  let lastChunkSequence: number | null = null;
  let finalResponseTraceId: string | undefined;

  const idleMs = options.streamIdleTimeoutMs ?? 0;
  let streamIdleTimeoutFired = false;
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  const clearIdle = () => {
    if (idleTimer !== null) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
  };
  const armIdle = () => {
    clearIdle();
    if (idleMs <= 0) return;
    idleTimer = setTimeout(() => {
      idleTimer = null;
      streamIdleTimeoutFired = true;
      const err = Object.assign(new Error('stream_idle_timeout'), {
        code: 'STREAM_IDLE_TIMEOUT',
      });
      options.onError?.(err);
      void reader.cancel();
    }, idleMs);
  };

  try {
    armIdle();
    while (true) {
      let readResult: ReadableStreamReadResult<Uint8Array>;
      try {
        readResult = await reader.read();
      } catch (readErr) {
        clearIdle();
        if (streamIdleTimeoutFired) {
          throw Object.assign(new Error('stream_idle_timeout'), {
            code: 'STREAM_IDLE_TIMEOUT',
          });
        }
        throw readErr;
      }

      const { done, value } = readResult;
      if (streamIdleTimeoutFired) {
        clearIdle();
        throw Object.assign(new Error('stream_idle_timeout'), {
          code: 'STREAM_IDLE_TIMEOUT',
        });
      }

      if (done) {
        clearIdle();
        break;
      }

      armIdle();

      buffer += decoder.decode(value, { stream: true });
      
      const events = buffer.split('\n\n');
      buffer = events.pop() || '';
      
      for (const event of events) {
        if (!event.trim()) continue;
        
        try {
          const jsonData = isAgentMode ? parseLangGraphSSE(event) : parseStandardSSE(event);
          if (!jsonData) continue;
          
          if (isAgentMode) {
            // MODO AGENTE - Procesar actualizaciones de agentes
            if (jsonData.agentUpdate) {
              const { agent, thought, is_final } = jsonData.agentUpdate;
              
              // 🔄 NUEVO: Guardar CADA pensamiento como una entrada separada
              if (thought && typeof thought === 'string' && thought.trim()) {
                const cleanThought = thought.trim()
                  .replace(/[ \t]+/g, ' ') // Preservar saltos de línea, solo normalizar espacios horizontales
                  .trim();
                
                if (cleanThought) {
                  // 🆕 GUARDAR VERSION CRUDA (tal cual viene del backend)
                  rawSteps.push({
                    type: "thought",
                    agent: agent,
                    order: completionCounter,
                    message: cleanThought,
                    metadata: {
                      is_final: is_final || false,
                      timestamp: new Date().toISOString()
                    }
                  });
                  
                  const completedThought = {
                    agent,
                    thought: cleanThought,
                    completion_order: completionCounter++
                  };
                  
                  // Guardar pensamiento (ya sea is_final: true o false)
                  completedThoughts.push(completedThought);
                  
                  console.log('💭 [SSE Processor] Pensamiento guardado:', {
                    agent,
                    isFinal: is_final,
                    thought: cleanThought.substring(0, 50) + '...',
                    order: completedThought.completion_order,
                    totalThoughts: completedThoughts.length,
                    totalRawSteps: rawSteps.length,
                    completedThought: completedThought,
                    allCompletedThoughts: completedThoughts
                  });
                  
                  // Notificar a la UI sobre el pensamiento
                  if (options.onAgentUpdate) {
                    options.onAgentUpdate({
                      agent,
                      thought: cleanThought,
                      is_final: is_final || false,
                      completion_order: completedThought.completion_order
                    });
                  }
                  
                  // Para compatibilidad con onTokenUpdate
                  if (options.onTokenUpdate) {
                    options.onTokenUpdate(agent, cleanThought, is_final || false);
                  }
                }
              }
            }
            
            // 🆕 NUEVO: Procesar cambios OOXML en agent thoughts
            if (jsonData.agentUpdate?.changes) {
              const changes = jsonData.agentUpdate.changes;
              
              // Validar que sea un string no vacío
              if (typeof changes === 'string' && changes.trim()) {
                console.log('🔄 [SSE Agent] Cambios OOXML detectados en agent thought:', {
                  agent: jsonData.agentUpdate.agent,
                  changesLength: changes.length,
                  thoughtPreview: jsonData.agentUpdate.thought.substring(0, 50) + '...'
                });
                
                // Notificar callback para aplicar cambios inmediatamente
                if (options.onAgentChanges) {
                  options.onAgentChanges(
                    jsonData.agentUpdate.agent,
                    changes,
                    jsonData.agentUpdate.thought
                  );
                }
              } else if (changes !== null && changes !== undefined) {
                // Manejar casos donde changes venga como array vacío u otro tipo
                console.warn('⚠️ [SSE Agent] Changes field received but is not a valid string:', {
                  type: typeof changes,
                  value: changes
                });
              }
            }
            
            // 🆕 Mostrar toolResult como un pensamiento del agente
            if (jsonData.toolResult) {
              const tr: any = jsonData.toolResult;
              const toolName = tr.tool_name || tr.metadata?.tool_name || 'tool';
              const agent = tr.agent || 'tool';

              let message = '';
              if (tr.success === false) {
                const err = tr.metadata?.error_message || tr.content || 'Error';
                message = `❌ ${toolName}: ${String(err)}`;
              } else {
                const content = typeof tr.content === 'string' ? tr.content : (tr.metadata?.content || '');
                message = `${String(content || '')}`;
              }

              const cleanThought = String(message).trim();
              if (cleanThought) {
                // 🆕 GUARDAR VERSION CRUDA (tal cual viene del backend)
                rawSteps.push({
                  type: "tool_call",
                  agent: toolName, // En formato crudo, agent es el nombre de la herramienta
                  order: completionCounter,
                  output: {
                    content: tr.content || '',
                    success: tr.success !== false,
                    metadata: tr.metadata || {}
                  },
                  metadata: {
                    timestamp: tr.timestamp || new Date().toISOString(),
                    tool_name: toolName,
                    tool_call_id: tr.tool_call_id
                  }
                });
                
                const completedThought = {
                  agent,
                  thought: cleanThought,
                  completion_order: completionCounter++,
                  toolResult: tr // 🆕 PRESERVAR toolResult completo
                };

                completedThoughts.push(completedThought);
                
                console.log('🔧 [SSE Processor] ToolResult guardado:', {
                  agent,
                  toolName,
                  thought: cleanThought.substring(0, 50) + '...',
                  order: completedThought.completion_order,
                  totalThoughts: completedThoughts.length,
                  totalRawSteps: rawSteps.length,
                  completedThought: completedThought,
                  toolResult: tr,
                  allCompletedThoughts: completedThoughts
                });

                if (options.onAgentUpdate) {
                  options.onAgentUpdate({
                    agent,
                    thought: cleanThought,
                    is_final: false,
                    completion_order: completedThought.completion_order
                  });
                }
                if (options.onTokenUpdate) {
                  options.onTokenUpdate(agent, cleanThought, false, tr); // 🆕 Pasar toolResult completo
                }
              }
              
              // 🆕 NUEVO: Procesar cambios OOXML en toolResult
              if (tr.changes) {
                const changes = tr.changes;
                
                // Validar que sea un string no vacío
                if (typeof changes === 'string' && changes.trim()) {
                  console.log('🔄 [SSE Agent] Cambios OOXML detectados en toolResult:', {
                    agent: agent,
                    toolName: toolName,
                    changesLength: changes.length,
                    contentPreview: cleanThought.substring(0, 50) + '...'
                  });
                  
                  // Notificar callback para aplicar cambios inmediatamente
                  if (options.onAgentChanges) {
                    options.onAgentChanges(
                      agent,
                      changes,
                      cleanThought
                    );
                  }
                } else if (changes !== null && changes !== undefined) {
                  // Manejar casos donde changes venga como array vacío u otro tipo
                  console.warn('⚠️ [SSE Agent] Changes field in toolResult received but is not a valid string:', {
                    type: typeof changes,
                    value: changes
                  });
                }
              }
            }

            // Procesar tokens de respuesta en modo agente
            if (jsonData.token !== undefined) {
              finalResponse.response += jsonData.token;
              if (options.onToken) options.onToken(jsonData.token);
            }

            // Procesar chunks incrementales de respuesta final
            if (jsonData.final_response_chunk) {
              const chunkPayload = jsonData.final_response_chunk;
              const chunk = typeof chunkPayload.chunk === 'string' ? chunkPayload.chunk : '';
              const sequence = typeof chunkPayload.sequence === 'number' ? chunkPayload.sequence : undefined;
              const timestamp = chunkPayload.timestamp;
              const chunkTraceId = chunkPayload.trace_id;

              if (chunkTraceId) {
                finalResponseTraceId = chunkTraceId;
              }

              // Ignorar chunks vacíos pero preservar logs útiles de trazabilidad
              if (chunk.length === 0) {
                console.log('ℹ️ [SSE Agent] final_response_chunk vacío ignorado');
              } else {
                streamedFinalText += chunk;
                finalChunkCount += 1;

                if (sequence !== undefined) {
                  if (lastChunkSequence !== null && sequence <= lastChunkSequence) {
                    console.warn('⚠️ [SSE Agent] sequence no monotónico en final_response_chunk:', {
                      previousSequence: lastChunkSequence,
                      receivedSequence: sequence
                    });
                  }
                  lastChunkSequence = sequence;
                }

                if (options.onFinalResponseChunk) {
                  options.onFinalResponseChunk({
                    chunk,
                    streamedText: streamedFinalText,
                    trace_id: chunkTraceId,
                    sequence,
                    timestamp,
                    finalChunkCount,
                    lastChunkSequence
                  });
                }
              }
            }
            
            // Procesar respuesta final del agente
            if (jsonData.final_response) {
              console.log('🎯 [SSE Agent] Final response recibida del backend:', {
                responseLength: jsonData.final_response.response?.length || 0,
                hasSources: !!jsonData.final_response.sources,
                sourcesCount: jsonData.final_response.sources?.length || 0,
                hasChanges: !!jsonData.final_response.changes,
                changesLength: jsonData.final_response.changes?.length || 0,
                traceId: jsonData.final_response.trace_id,
                fullResponse: jsonData.final_response
              });
              
              let cleanResponse = jsonData.final_response.response || '';
              if (cleanResponse.includes('\\u00')) {
                try {
                  cleanResponse = cleanResponse.replace(/\\u([0-9a-fA-F]{4})/g, (_, code) => {
                    return String.fromCharCode(parseInt(code, 16));
                  });
                } catch (e) {
                  console.warn('⚠️ Error decodificando Unicode en respuesta:', e);
                }
              }
              
              // Actualizar fuentes, cambios y trace_id
              finalResponse = {
                ...finalResponse,
                response: cleanResponse,
                sources: jsonData.final_response.sources || [],
                changes: typeof jsonData.final_response.changes === 'string' ? jsonData.final_response.changes : '',
                trace_id: jsonData.final_response.trace_id || finalResponseTraceId
              };

              // El backend define final_response como autoridad
              if (streamedFinalText && streamedFinalText !== cleanResponse) {
                console.warn('⚠️ [SSE Agent] Diferencia entre chunks y final_response; se usará final_response como fuente de verdad', {
                  streamedLength: streamedFinalText.length,
                  finalLength: cleanResponse.length
                });
              }
              
              console.log('📦 [SSE Agent] Final response procesada:', {
                responseLength: finalResponse.response.length,
                sourcesCount: finalResponse.sources.length,
                changesLength: finalResponse.changes.length,
                changesPreview: finalResponse.changes ? finalResponse.changes.substring(0, 200) + '...' : 'N/A',
                traceId: finalResponse.trace_id
              });
              
              // Llamar onComplete
              if (options.onComplete) {
                options.onComplete({
                  ...finalResponse,
                  completedThoughts,
                  streamedFinalText,
                  finalChunkCount,
                  lastChunkSequence
                });
              }
            }
          } else {
            // MODO NORMAL - Procesar tokens, fuentes y cambios
            if (!sourcesProcessed && jsonData.sources?.length) {
              sources = jsonData.sources;
              console.log('📚 [SSE] Sources recibidas del backend:', sources);
              if (options.onSources) options.onSources(sources);
              sourcesProcessed = true;
            }
            
            // 🆕 NUEVO: Capturar trace_id en modo normal
            // Puede venir dentro de final_response (si el backend lo envía así)
            if (jsonData.final_response?.trace_id) {
              traceId = jsonData.final_response.trace_id;
              console.log('🏷️ [SSE] trace_id detectado en final_response:', traceId);
            }
            // O puede venir a nivel raíz (si el backend lo envía así)
            else if ((jsonData as any).trace_id) {
              traceId = (jsonData as any).trace_id;
              console.log('🏷️ [SSE] trace_id detectado en root:', traceId);
            }
            
            if (!changesProcessed && jsonData.changes && typeof jsonData.changes === 'string' && jsonData.changes.trim()) {
              changes = jsonData.changes;
              console.log('🔄 [SSE] Changes (OOXML) recibidos del backend:', {
                length: changes.length,
                preview: changes.substring(0, 200) + '...',
                fullChanges: changes
              });
              if (options.onChanges) options.onChanges(changes);
              changesProcessed = true;
            }
            
            if (jsonData.token !== undefined) {
              completeContent += jsonData.token;
              if (options.onToken) options.onToken(jsonData.token);
            }
            
            if (jsonData.done) {
              console.log('✅ [SSE] Stream completado en modo normal');
              if (options.onComplete) options.onComplete();
            }
          }
          
          // Error handling común
          if (jsonData.error) {
            console.error('❌ Error del stream:', jsonData.error);
            throw new Error(jsonData.error);
          }
          
        } catch (e) {
          console.error('❌ Error procesando evento SSE:', e);
        }
      }
    }
  } finally {
    clearIdle();
    reader.releaseLock();
  }
  
  // Retornar resultado según el modo
  if (isAgentMode) {
    return {
      content: finalResponse.response,
      sources: finalResponse.sources,
      changes: finalResponse.changes,
      completedThoughts,
      rawSteps, // 🆕 Incluir raw steps
      trace_id: finalResponse.trace_id || finalResponseTraceId
    };
  } else {
    return {
      content: completeContent,
      sources,
      changes,
      trace_id: traceId
    };
  }
}

/**
 * Parsear SSE estándar (modo normal)
 */
function parseStandardSSE(rawEvent: string): StreamingMessage | null {
  try {
    const dataLines = rawEvent.split('\n')
      .filter(line => line.startsWith('data: '))
      .map(line => line.slice(6))
      .join('');
      
    if (!dataLines) return null;
    
    const parsed = JSON.parse(dataLines) as StreamingMessage;
    
    // 📋 LOG: Evento SSE parseado
    console.log('📋 [SSE Standard] Evento parseado:', {
      hasSources: !!parsed.sources,
      sourcesCount: parsed.sources?.length || 0,
      hasToken: !!parsed.token,
      tokenLength: parsed.token?.length || 0,
      hasChanges: !!parsed.changes,
      changesLength: parsed.changes?.length || 0,
      hasFinalResponseChunk: !!parsed.final_response_chunk,
      isDone: !!parsed.done,
      hasError: !!parsed.error,
      fullData: parsed
    });
    
    return parsed;
  } catch (e) {
    console.error('❌ Error parsing standard SSE:', e);
    return null;
  }
}

/**
 * Parsear SSE de LangGraph (modo agente)
 */
function parseLangGraphSSE(rawEvent: string): StreamingMessage | null {
  const dataLines = rawEvent.split('\n')
    .filter(line => line.startsWith('data: '))
    .map(line => line.slice(6));
  
  if (dataLines.length === 0) return null;

  try {
    const combined = dataLines.join('');
    
    try {
      const parsed = JSON.parse(combined);
      
      // 📋 LOG: Evento LangGraph parseado (JSON directo)
      console.log('📋 [SSE LangGraph] Evento parseado (JSON):', {
        hasAgentUpdate: !!parsed.agentUpdate,
        agent: parsed.agentUpdate?.agent,
        isFinal: parsed.agentUpdate?.is_final,
        hasToken: !!parsed.token,
        hasFinalResponse: !!parsed.final_response,
        hasFinalResponseChunk: !!parsed.final_response_chunk,
        hasChanges: !!parsed.final_response?.changes,
        changesLength: parsed.final_response?.changes?.length || 0,
          hasToolResult: !!parsed.toolResult,
        fullData: parsed
      });
      
      return parsed;
    } catch (e) {
      const cleanData = combined.replace(/"([^"]+)":/g, '"$1":');
      
      try {
        const parsed = JSON.parse(cleanData);
        
        // 📋 LOG: Evento LangGraph parseado (JSON limpio)
        console.log('📋 [SSE LangGraph] Evento parseado (limpio):', {
          hasAgentUpdate: !!parsed.agentUpdate,
          hasToken: !!parsed.token,
          hasFinalResponse: !!parsed.final_response,
          hasFinalResponseChunk: !!parsed.final_response_chunk,
          fullData: parsed
        });
        
        return parsed;
      } catch (e) {
        const result: StreamingMessage = {};
        
        // Extract agent update
        const agentUpdateMatch = combined.match(/{.*?"agentUpdate":\s*{(.*?)},/);
        if (agentUpdateMatch && agentUpdateMatch[1]) {
          const agentInfo = agentUpdateMatch[1];
          
          const agentMatch = agentInfo.match(/"agent":\s*"([^"]*)"/);
          const thoughtMatch = agentInfo.match(/"thought":\s*"([^"]*)"/);
          const isFinalMatch = agentInfo.match(/"is_final":\s*(true|false)/);
          const changesMatch = agentInfo.match(/"changes":\s*"([^"]*)"/);
          
          result.agentUpdate = {
            agent: agentMatch ? agentMatch[1] : '',
            thought: thoughtMatch ? thoughtMatch[1] : '',
            is_final: isFinalMatch ? isFinalMatch[1] === 'true' : false,
            changes: changesMatch ? changesMatch[1] : undefined
          };
        }
        
        // Extract final response
        const finalResponseMatch = combined.match(/"final_response"\s*:\s*(\{[\s\S]*\})/);
        if (finalResponseMatch && finalResponseMatch[1]) {
          try {
            const finalResponseObj = JSON.parse(finalResponseMatch[1]);
            result.final_response = {
              response: finalResponseObj.response || '',
              sources: finalResponseObj.sources || [],
              changes: finalResponseObj.changes || '',  // ← Mantener como string
              trace_id: finalResponseObj.trace_id
            };
          } catch (e) {
            // Fallback más robusto: Intentar parsear todo el combined como JSON
            console.warn('⚠️ Regex fallback no pudo parsear final_response, intentando parseo completo...');
            try {
              const fullParsed = JSON.parse(combined);
              if (fullParsed.final_response) {
                result.final_response = {
                  response: fullParsed.final_response.response || '',
                  sources: fullParsed.final_response.sources || [],
                  changes: fullParsed.final_response.changes || '',
                  trace_id: fullParsed.final_response.trace_id
                };
              }
            } catch (err) {
              console.error('❌ No se pudo parsear final_response de ninguna manera:', err);
              console.error('Combined data:', combined);
            }
          }
        }

        // Extract final response chunk
        const finalResponseChunkMatch = combined.match(/"final_response_chunk"\s*:\s*\{([^}]*)\}/);
        if (finalResponseChunkMatch && finalResponseChunkMatch[1]) {
          const chunkInfo = finalResponseChunkMatch[1];
          try {
            const chunkObj = JSON.parse(`{${chunkInfo}}`);
            result.final_response_chunk = {
              chunk: typeof chunkObj.chunk === 'string' ? chunkObj.chunk : '',
              trace_id: typeof chunkObj.trace_id === 'string' ? chunkObj.trace_id : undefined,
              sequence: typeof chunkObj.sequence === 'number' ? chunkObj.sequence : undefined,
              timestamp: typeof chunkObj.timestamp === 'string' ? chunkObj.timestamp : undefined
            };
          } catch (chunkParseError) {
            console.warn('⚠️ No se pudo parsear final_response_chunk en regex fallback:', chunkParseError);
          }
        }
        
        // Extract token
        const tokenMatch = combined.match(/"token":\s*"([^"]*)"/);
        if (tokenMatch) {
          result.token = tokenMatch[1];
        }
        
        // Extract done flag
        const doneMatch = combined.match(/"done":\s*(true|false)/);
        if (doneMatch) {
          result.done = doneMatch[1] === 'true';
        }
        
        // 📋 LOG: Evento LangGraph parseado (regex fallback)
        console.log('📋 [SSE LangGraph] Evento parseado (regex):', {
          hasAgentUpdate: !!result.agentUpdate,
          hasToken: !!result.token,
          hasFinalResponse: !!result.final_response,
          hasFinalResponseChunk: !!result.final_response_chunk,
          fullData: result
        });
        
        return result;
      }
    }
  } catch (e) {
    console.error('❌ Failed to parse LangGraph SSE event:', e);
    return null;
  }
}








